import { useCallback, useRef } from 'react';

import { link } from '@aline/routes';
import { useNavigate } from 'react-router-dom';
import type { OperationErrorContext } from 'src/gql/apollo-error-link';
import { Apollo } from 'src/gql/generated';
import { useDispatchNotification, useDispatchSuccessSnack } from 'src/helpers';

import { SKILLS_LIST_REFETCH } from './skill-editor-errors';

const CREATE_ERROR_MESSAGE = 'Could not create the skill. Please try again.';

/**
 * Creates a blank skill (an empty SKILL.md, private to its author) and opens it in the editor.
 *
 * The server saves an unfinished skill without complaint and keeps it out of the `/`
 * picker until it has a name and a description, so there is nothing to fill in first.
 */
export function useCreateSkillAndEdit() {
  const navigate = useNavigate();
  const creatingRef = useRef(false);
  const [createAgentSkill, { loading }] = Apollo.useCreateAgentSkillMutation({
    errorPolicy: 'none',
    refetchQueries: SKILLS_LIST_REFETCH,
  });
  const dispatchCreatedSnack = useDispatchSuccessSnack({ message: 'Skill created.' });
  const dispatchErrorSnack = useDispatchNotification({
    message: CREATE_ERROR_MESSAGE,
    variant: 'error',
  });

  const createSkillAndEdit = useCallback(async () => {
    if (creatingRef.current) return;
    creatingRef.current = true;
    let errorHandled = false;
    try {
      const { data } = await createAgentSkill({
        context: {
          genericErrorMessage: CREATE_ERROR_MESSAGE,
          onErrorHandled: () => {
            errorHandled = true;
          },
        } satisfies OperationErrorContext,
        variables: { input: { content: '', scope: Apollo.AgentSkillScope.Personal } },
      });
      if (!data) throw new Error('The server did not confirm skill creation.');
      dispatchCreatedSnack();
      navigate(link.AdminAgentSkillDetail({ skillId: data.createAgentSkill.agentSkill.id }));
    } catch {
      if (!errorHandled) dispatchErrorSnack();
    } finally {
      creatingRef.current = false;
    }
  }, [createAgentSkill, dispatchCreatedSnack, dispatchErrorSnack, navigate]);

  return { createSkillAndEdit, loading };
}
